import { useState, useRef } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import styled from 'styled-components';
import { medias } from 'assets/styled/tokens/medias';

interface CheckboxOption {
  id: string;
  label: string;
  selected?: boolean;
}

interface DropdownCheckboxMenuProps {
  title: string;
  items: CheckboxOption[];
  onChangeItem?: (itemId: string, isSelected?: boolean) => void;
  onToggleAll?: (isAllSelected: boolean) => void;
}

const MenuContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 200px;
  max-width: 400px;
  user-select: none;
`;

const DropdownButton = styled.button<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  /* background-color: white; */
  color: #333;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1;
  position: relative;
  gap: 10px;

  &:hover {
    background-color: #f9f9f9;
  }

  @media (min-width: ${medias.tablet}) {
    ${props =>
      props.isOpen &&
      `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom: 1px solid #e2e2e2;
    `}
  }
`;

const Backdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
  transition: opacity 0.3s ease;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};

  @media (min-width: ${medias.tablet}) {
    display: none;
  }
`;

const BodyScrollLock = styled.div<{ isOpen: boolean }>`
  @media (max-width: ${medias.tablet}) {
    ${props =>
      props.isOpen &&
      `
      & ~ body {
        overflow: hidden;
        position: fixed;
        width: 100%;
        height: 100%;
      }
    `}
  }
`;

const OptionsContainer = styled.div<{ isOpen: boolean }>`
  background-color: white;
  overflow-y: auto;

  @media (max-width: ${medias.tablet}) {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    max-height: 80vh;
    transform: translateY(${props => (props.isOpen ? '0' : '100%')});
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    z-index: 99999999;
    transition: transform 0.3s ease;
  }

  @media (min-width: ${medias.tablet}) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-height: ${props => (props.isOpen ? '300px' : '0')};
    border: ${props => (props.isOpen ? '1px solid #e2e2e2' : 'none')};
    border-top: none;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    box-shadow: ${props => (props.isOpen ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none')};
    z-index: 10;
    transition: all 0.2s ease;
    opacity: ${props => (props.isOpen ? '1' : '0')};
    visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #eaeaea;
  background-color: white;

  @media (min-width: ${medias.tablet}) {
    display: none;
  }
`;

const MobileTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #666;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const OptionItem = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
  color: #555;
  background-color: ${props => (props.isSelected ? '#f0f7ff' : 'transparent')};

  &:hover {
    background-color: ${props => (props.isSelected ? '#e6f0ff' : '#f5f5f5')};
  }
`;

const Checkbox = styled.div<{ isSelected?: boolean; isPartialSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  width: 16px;
  height: 16px;
  border: 1px solid ${props => (props.isSelected ? '#F38A02' : '#ccc')};
  border-radius: 3px;
  background-color: ${props => (props.isSelected ? '#F38A02' : 'white')};
  position: relative;

  &:after {
    content: '';
    display: ${props => (props.isSelected && !props.isPartialSelected ? 'block' : 'none')};
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    position: absolute;
    top: 1px;
  }

  &:before {
    content: '';
    display: ${props => (props.isPartialSelected ? 'block' : 'none')};
    width: 8px;
    height: 2px;
    background-color: white;
    position: absolute;
  }
`;

export const FiltersDropdown = ({ title, items, onChangeItem, onToggleAll }: DropdownCheckboxMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localItems, setLocalItems] = useState<CheckboxOption[]>(items);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleItemClick = (id: string, index: number) => {
    // Get current selection state to toggle
    const currentItem = localItems[index];
    const newSelectedState = !currentItem.selected;

    // Update local state
    const updatedItems = [...localItems];
    updatedItems[index] = { ...currentItem, selected: newSelectedState };
    setLocalItems(updatedItems);

    // If it's the first item (select all)
    if (index === 0) {
      // Update all other items
      const newItems = updatedItems.map((item, i) => (i === 0 ? item : { ...item, selected: newSelectedState }));
      setLocalItems(newItems);

      // Call the onToggleAll callback
      if (onToggleAll) {
        onToggleAll(newSelectedState);
      }
    } else {
      // Call the onChangeItem callback for individual items
      if (onChangeItem) {
        onChangeItem(id, newSelectedState);
      }

      // Check if all items (except first) are selected to update "select all"
      const allSelected = updatedItems.filter((_, i) => i !== 0).every(item => item.selected);

      // Update select all checkbox if needed
      if (updatedItems[0].selected !== allSelected) {
        updatedItems[0] = { ...updatedItems[0], selected: allSelected };
        setLocalItems(updatedItems);
      }
    }
  };

  // Get the number of selected options (excluding "Select All")
  const selectedCount = localItems.filter((_, index) => index !== 0 && localItems[index].selected).length;

  // Determine what text to show in the dropdown button
  const buttonText = selectedCount > 0 ? `${title} (${selectedCount})` : title;

  return (
    <>
      <BodyScrollLock isOpen={isOpen} />
      <Backdrop isOpen={isOpen} onClick={closeDropdown} />

      <MenuContainer ref={dropdownRef}>
        <DropdownButton isOpen={isOpen} onClick={toggleDropdown}>
          <span>{buttonText}</span>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </DropdownButton>

        <OptionsContainer isOpen={isOpen}>
          <MobileHeader>
            <MobileTitle>{title}</MobileTitle>
            <CloseButton onClick={closeDropdown}>
              <X size={20} />
            </CloseButton>
          </MobileHeader>

          {localItems.map((item, index) => (
            <OptionItem key={item.id} isSelected={item.selected} onClick={() => handleItemClick(item.id, index)}>
              <Checkbox isSelected={item.selected} />
              {item.label}
            </OptionItem>
          ))}
        </OptionsContainer>
      </MenuContainer>
    </>
  );
};
